import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  People as PeopleIcon,
  Repeat as RepeatIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  EventRepeat as EventIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import api from '../../services/api';
import {
  StatCard,
  SectionLoading,
  SectionError,
  COLORS,
  formatNumber,
  formatCurrency,
  formatPct,
  formatMonth,
  heatmapColor,
  heatmapText,
} from './shared';

interface Summary {
  total_customers: number;
  eligible_customers: number;
  retained_customers: number;
  retention_rate_90d: number;
  repeat_customers: number;
  repeat_share: number;
  avg_orders_per_customer: number;
  avg_ltv: number;
  total_ltv: number;
}

interface TrendRow {
  month: string;
  cohort_size: number;
  retained: number;
  retention_pct: number;
}

interface CohortRow {
  cohort_month: string;
  cohort_size: number;
  months: Record<string, { returned: number; pct: number }>;
}

interface RevenueSplitRow {
  month: string;
  new_revenue: number;
  repeat_revenue: number;
  new_orders: number;
  repeat_orders: number;
  repeat_share: number;
}

interface DaysToSecond {
  buckets: { bucket: string; count: number; avg_days: number }[];
  median_days: number;
}

const RetentionSection: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [trend, setTrend] = useState<TrendRow[]>([]);
  const [cohorts, setCohorts] = useState<CohortRow[]>([]);
  const [revenueSplit, setRevenueSplit] = useState<RevenueSplitRow[]>([]);
  const [daysToSecond, setDaysToSecond] = useState<DaysToSecond | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get('/api/retention/summary'),
      api.get('/api/retention/trend'),
      api.get('/api/retention/cohorts'),
      api.get('/api/retention/revenue-split'),
      api.get('/api/retention/days-to-second'),
    ])
      .then(([s, t, c, rs, dts]) => {
        setSummary(s.data);
        setTrend(t.data);
        setCohorts(c.data);
        setRevenueSplit(rs.data);
        setDaysToSecond(dts.data);
      })
      .catch((err) => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  if (loading) return <SectionLoading />;
  if (error) return <SectionError message={error} />;
  if (!summary) return <SectionError />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Headline stat cards */}
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="90 天留存率"
            value={formatPct(summary.retention_rate_90d)}
            subtitle={`${formatNumber(summary.retained_customers)} / ${formatNumber(summary.eligible_customers)} 名客戶`}
            icon={<RepeatIcon />}
            color={COLORS.success}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="回頭客數"
            value={formatNumber(summary.repeat_customers)}
            subtitle={`佔總客戶 ${formatPct(summary.repeat_share)}`}
            icon={<PeopleIcon />}
            color={COLORS.primary}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="平均訂單數 / 客"
            value={summary.avg_orders_per_customer.toFixed(2)}
            subtitle="LTV 代理指標"
            icon={<TrendingUpIcon />}
            color={COLORS.purple}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="平均客戶 LTV"
            value={formatCurrency(summary.avg_ltv)}
            subtitle={`總 LTV ${formatCurrency(summary.total_ltv)}`}
            icon={<MoneyIcon />}
            color={COLORS.warning}
          />
        </Grid>
      </Grid>

      {/* Retention trend line */}
      <Card variant="outlined" sx={{ border: `1px solid ${COLORS.border}` }}>
        <CardHeader
          title="留存率趨勢（過去 12 個月，以首購月份分組）"
          titleTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }}
          subheader="每個點代表該月首購客戶在 90 天內是否回購"
          subheaderTypographyProps={{ fontSize: '0.75rem' }}
        />
        <CardContent sx={{ pt: 0 }}>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trend.map((t) => ({ ...t, monthLabel: formatMonth(t.month) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="monthLabel" fontSize={11} />
              <YAxis
                yAxisId="left"
                fontSize={11}
                tickFormatter={(v) => `${v}%`}
                domain={[0, 'auto']}
              />
              <YAxis yAxisId="right" orientation="right" fontSize={11} />
              <Tooltip
                formatter={(v: any, name: string) =>
                  name === '留存率' ? `${v}%` : formatNumber(v)
                }
              />
              <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="retention_pct"
                name="留存率"
                stroke={COLORS.success}
                strokeWidth={2.5}
                dot={{ r: 4 }}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="cohort_size"
                name="首購人數"
                stroke={COLORS.primary}
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Cohort heatmap */}
      <Card variant="outlined" sx={{ border: `1px solid ${COLORS.border}` }}>
        <CardHeader
          title="Cohort 留存熱圖"
          titleTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }}
          subheader="橫軸=首購後第 N 個月，數字=該月份回購百分比"
          subheaderTypographyProps={{ fontSize: '0.75rem' }}
        />
        <CardContent sx={{ pt: 0 }}>
          {cohorts.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              資料不足
            </Typography>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: COLORS.bgAlt }}>Cohort</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: COLORS.bgAlt }} align="right">
                      人數
                    </TableCell>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <TableCell
                        key={m}
                        align="center"
                        sx={{ fontWeight: 600, bgcolor: COLORS.bgAlt }}
                      >
                        M+{m}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cohorts.map((c) => (
                    <TableRow key={c.cohort_month}>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {formatMonth(c.cohort_month)}
                      </TableCell>
                      <TableCell align="right">{formatNumber(c.cohort_size)}</TableCell>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                        const cell = c.months[String(m)];
                        const pct = cell?.pct || 0;
                        return (
                          <TableCell
                            key={m}
                            align="center"
                            sx={{
                              bgcolor: heatmapColor(pct),
                              color: heatmapText(pct),
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              p: '4px 8px',
                            }}
                          >
                            {pct > 0 ? `${pct}%` : '—'}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Revenue split */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ border: `1px solid ${COLORS.border}`, height: '100%' }}>
            <CardHeader
              title="新客 vs 回頭客營收"
              titleTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }}
              subheader="堆疊柱狀圖顯示每月營收組成"
              subheaderTypographyProps={{ fontSize: '0.75rem' }}
            />
            <CardContent sx={{ pt: 0 }}>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={revenueSplit.map((r) => ({ ...r, monthLabel: formatMonth(r.month) }))}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="monthLabel" fontSize={11} />
                  <YAxis
                    fontSize={11}
                    tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`}
                  />
                  <Tooltip formatter={(v: any) => formatCurrency(v as number)} />
                  <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                  <Bar
                    dataKey="new_revenue"
                    name="新客營收"
                    stackId="a"
                    fill={COLORS.primary}
                  />
                  <Bar
                    dataKey="repeat_revenue"
                    name="回頭客營收"
                    stackId="a"
                    fill={COLORS.success}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ border: `1px solid ${COLORS.border}`, height: '100%' }}>
            <CardHeader
              title="二次購買時間分佈"
              titleTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }}
              subheader={
                daysToSecond
                  ? `中位數 ${daysToSecond.median_days} 天 — 決定再行銷節奏`
                  : ''
              }
              subheaderTypographyProps={{ fontSize: '0.75rem' }}
            />
            <CardContent sx={{ pt: 0 }}>
              {daysToSecond && daysToSecond.buckets.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={daysToSecond.buckets}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="bucket" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip
                      formatter={(v: any, name: string, p: any) => [
                        `${v} 人（平均 ${p.payload.avg_days} 天）`,
                        '人數',
                      ]}
                    />
                    <Bar dataKey="count" fill={COLORS.purple} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 4, textAlign: 'center' }}
                >
                  資料不足
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RetentionSection;
